import { StyledPie } from '@/components/StyledPie';
import { CustomTable } from '@/components/Table';
import { toCurrency, toNumber } from '@/helpers/masks/toCurrency';
import { colors } from '@/styles/colors';
import React, { Fragment } from 'react';
import { ArrowLeft } from 'react-feather';
import { Link } from 'react-router-dom';
import { Card, Col, Container, Row } from 'reactstrap';
import { Dialog, Loading } from '@/components';
import { States } from '@/helpers/common/states';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { GatewayPaymentResponse } from '@/model/GatewayPaymentResponse';
import { ActionProps } from '@/components/Dialog';
import FilterVector from '@/assets/images/svg/FilterVector';
import { columns, ShouldShowModal } from '../../types';
import { FilterContent } from '../../components/FilterContent';

interface StateProps {
  state: States;
  report?: GatewayPaymentResponse;
  title: React.ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  onToggle: () => void;
  onChangeFormInput: OnChangeFormInput;
  onClearFilter: () => void;
  onFilter: () => void;
}

type Props = StateProps & DispatchProps;

export const GatewayPaymentContainer: React.FC<Props> = ({
  state,
  report,
  title,
  visible,
  onToggle,
  shouldShowModal,
  formData,
  onChangeFormInput,
  formErrors,
  onClearFilter,
  onFilter,
}): JSX.Element => {
  const renderActionDialogToCancelFilter: ActionProps = {
    title: 'Limpar',
    onClick: (): void => onClearFilter(),
    theme: 'noneBorder',
  };
  const dataTable =
    report && report.errors && report.errors.length > 0
      ? report.errors.map(item => ({
          amount: item.amount,
          code: item.code,
          auth: item.type,
          message: item.message,
          value: toCurrency(item.value),
        }))
      : [];
  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        position={shouldShowModal === ShouldShowModal.filter ? 'right' : 'center'}
        isContentWithCard={shouldShowModal !== ShouldShowModal.filter}
        actions={[
          {
            [ShouldShowModal.filter]: renderActionDialogToCancelFilter,
          }[shouldShowModal],
          {
            [ShouldShowModal.filter]: {
              title: 'Aplicar',
              onClick: () => onFilter(),
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.filter]: (
              <FilterContent
                formData={formData}
                formErrors={formErrors}
                onChangeFormInput={onChangeFormInput}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>

      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between mb-5">
          <div className="pageTitle d-flex">
            <Link to={`${process.env.PUBLIC_URL}/dashboard`}>
              <ArrowLeft color={colors.black} className="arrow-left" />
            </Link>
            <span className="ml-3 mb-0 mt-2">Pagamentos com Gateway</span>
          </div>
          <div className="button-filter-container">
            <div className="filter-container">
              <div
                className="filter-content"
                onClick={(): void => {
                  onToggle();
                }}
              >
                <FilterVector />
              </div>
            </div>
          </div>
        </div>
        {report && (
          <Fragment>
            {report.payments &&
              report.payments.length > 0 &&
              report.payments.map((payment, paymentIX) => (
                <Fragment key={`payment-${paymentIX}`}>
                  <h5 style={{ marginTop: paymentIX > 0 ? '40px' : '' }}>{payment.description}</h5>

                  <div
                    style={{
                      border: '1px solid #ccc',
                      borderRadius: '10px',
                      padding: '20px 10px 10px 10px',
                      display: 'inline-block',
                      width: '100%',
                      maxWidth: '800px',
                      // marginBottom: '20px',
                      background: '#fff',
                      boxShadow: '4px 4px 10px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {payment.informations &&
                      payment.informations.length > 0 &&
                      payment.informations.map((information, informationIX) => (
                        <Fragment key={`information-${informationIX}`}>
                          <Row
                            className="mb-4"
                            style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                          >
                            <Col md={4}>
                              <div className="text-darkgray-regular">{information.description}</div>
                            </Col>
                            <Col xs={4} style={{ textAlign: 'right' }}>
                              <div className="text-darkgray-regular">{`QTD: ${toNumber(
                                information.amount,
                              )}`}</div>
                            </Col>
                            <Col xs={4} style={{ textAlign: 'right' }}>
                              <div
                                className={information.positive ? 'text-success' : 'text-danger'}
                              >
                                {toCurrency(information.value)}
                              </div>
                            </Col>
                          </Row>
                        </Fragment>
                      ))}
                  </div>
                  {payment.card3DSChart &&
                    payment.card3DSChart.length > 0 &&
                    payment.cardNormalChart &&
                    payment.cardNormalChart.length > 0 && (
                      <Row>
                        <Col md={6}>
                          <h6 className="mb-4 mt-5">3DS</h6>
                          <StyledPie generalSale={payment.card3DSChart} type="currency" />
                        </Col>
                        <Col md={6}>
                          <h6 className="mb-4 mt-5">Normal</h6>
                          <StyledPie generalSale={payment.cardNormalChart} type="currency" />
                        </Col>
                      </Row>
                    )}
                  {payment.pixChart && payment.pixChart.length > 0 && (
                    <Row>
                      <Col md={6}>
                        <h6 className="mb-4 mt-5">PIX</h6>
                        <StyledPie generalSale={payment.pixChart} type="currency" />
                      </Col>
                    </Row>
                  )}
                </Fragment>
              ))}

            <h5 style={{ marginTop: '40px' }}>Informações Sobre Não Autorização</h5>
            <Card
              className="mb-5 card-no-border customTableScroll"
              style={{ border: 'none', overflow: 'auto' }}
            >
              <div className="p-4">
                <CustomTable
                  columns={columns}
                  data={dataTable}
                  numberRowsPerPage={0}
                  theme="secondary"
                  progressPending={false}
                />
              </div>
            </Card>
          </Fragment>
        )}
      </Container>
    </Fragment>
  );
};
